// Queues an effect beat after every cast, whoever cast it, and a beat for
// every event the rules fire: a blast, a wake, a random move, a captor removed, a
// promotion. The source lights, a spark runs to each piece that changed, and
// the caption says what happened. The director plays them once the stage is
// clear.

import { BEAT_EFFECT_MS, BEAT_EVENT_MS, BEAT_STEP_MS } from '../constants';
import type { CastRecord, CastStep, EventRecord, GameState } from '../game/state';
import { spellById } from '../spells/library';
import { clearHauls, holdHaul, startHaul } from './captureEnergy';
import { type Beat, type BeatTone, beat, dropQueue, queueBeat } from './director';
import { gameStore } from './game';
import { showLastingOf } from './lastingShown';
import { keepPiecesWaiting, letAllPiecesGo, letPiecesGo } from './waitingPieces';

let seenCast = 0;
let seenEvent = 0;

const effectBeat = (cast: CastRecord): Beat => {
  const spell = spellById(cast.spellId);
  const changed = cast.squares.filter((square) => square !== cast.source);
  const marks = [
    ...(cast.source !== null ? [{ square: cast.source, kind: 'lift' as const }] : []),
    ...changed.map((square) => ({ square, kind: 'changed' as const })),
  ];
  const sparks =
    cast.source !== null ? changed.map((to) => ({ from: cast.source as number, to })) : [];
  return beat(cast.hadNoEffect ? `${spell.name} has no effect` : `${spell.name} lands`, cast.line, {
    marks,
    sparks,
    tone: spell.polarity,
    spotlight: marks.length > 0,
  });
};

/** One step of a cast: the pieces that move now, each with its two squares lit. */
const stepBeat = (cast: CastRecord, step: CastStep, index: number): Beat => {
  const spell = spellById(cast.spellId);
  // The name of the spell is on the card. With the name, the count did not fit in the cue.
  return beat(`Step ${index + 1} of ${cast.steps.length}`, step.text, {
    marks: step.moves.flatMap((move) => [
      { square: move.from, kind: 'lift' as const },
      { square: move.to, kind: 'changed' as const },
    ]),
    tone: spell.polarity,
    spotlight: true,
  });
};

/**
 * A cast with steps plays one beat for each step in place of the effect
 * beat. Every piece waits on its first square until its step starts.
 */
const queueSteps = (cast: CastRecord): void => {
  keepPiecesWaiting(cast.steps.flatMap((step) => step.moves));
  cast.steps.forEach((step, index) => {
    queueBeat(stepBeat(cast, step, index), BEAT_STEP_MS, () => letPiecesGo(step.moves));
  });
};

/** Log tones that color a beat. The rest fall back to the system color. */
const EVENT_TONES: Partial<Record<EventRecord['tone'], BeatTone>> = {
  ally: 'ally',
  enemy: 'enemy',
  any: 'any',
  board: 'board',
  capture: 'capture',
};

const eventBeat = (event: EventRecord): Beat =>
  beat(event.caption, event.text, {
    marks: [
      ...event.squares.map((square) => ({ square, kind: 'changed' as const })),
      ...event.aside.map((square) => ({ square, kind: 'land' as const })),
    ],
    tone: EVENT_TONES[event.tone] ?? 'system',
    spotlight: event.squares.length > 0,
  });

/** Marks every cast and event in a state as played, so a match that loads from a save does not play them again. */
export const markNarrated = (state: GameState): void => {
  seenCast = state.lastCast?.serial ?? 0;
  seenEvent = state.events[state.events.length - 1]?.serial ?? 0;
  letAllPiecesGo();
  clearHauls();
  showLastingOf(state);
};

/** Call once at startup. */
export const startNarrator = (): void => {
  gameStore.subscribe((state) => {
    if (!state.lastCast && state.events.length === 0) {
      seenCast = 0;
      seenEvent = 0;
      dropQueue();
      letAllPiecesGo();
      clearHauls();
      return;
    }
    // An event from before the cast comes first: a random move at the start
    // of a turn happens before the roll that follows it. An event the cast
    // made comes after it: a copy from a clone square the spell put a piece on.
    const cast = state.lastCast;
    const isCastNew = cast !== null && cast.serial !== seenCast;
    // The seals and the bands of the lasting effects change when a beat of the change starts.
    const showLasting = (): void => showLastingOf(state);
    const queueEvents = (before: number): void => {
      for (const event of state.events) {
        if (event.serial <= seenEvent || event.serial >= before) continue;
        seenEvent = event.serial;
        // The energy of a capture shows when its coins drop, from the start of this beat.
        holdHaul(event);
        queueBeat(eventBeat(event), BEAT_EVENT_MS, () => {
          showLasting();
          startHaul(event.serial);
        });
      }
    };
    if (cast && isCastNew) {
      queueEvents(cast.firstEvent);
      seenCast = cast.serial;
      if (cast.steps.length > 0) queueSteps(cast);
      else queueBeat(effectBeat(cast), BEAT_EFFECT_MS, showLasting);
    }
    queueEvents(Number.POSITIVE_INFINITY);
  });
};
